import { Info as InfoIcon } from '@mui/icons-material';
import {
	Alert,
	Button,
	IconButton,
	InputAdornment,
	Stack,
	TextField,
	Tooltip,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FieldConfiguration } from '../../api/ConfigApi';
import { handleUnauthorizedResponse } from '../../api/http';
import {
	AuthContext,
	AuthData,
	HIDE_PERIL_LIMITS_ENABLE,
} from '../../context/auth';
import { ClaimContext, ClaimData } from '../../context/claim';
import { PolicyContext, PolicyData } from '../../context/policy';
import { PRIMARY_BUTTON_STYLE } from '../../data/constants';
import {
	ADD_SUBCLAIM_VIEW_EXPENSE_RESERVE_LABEL,
	ADD_SUBCLAIM_VIEW_LOSS_RESERVE_LABEL,
	ADD_SUBCLAIM_VIEW_LOSS_RESERVE_TOOLTIP_MESSAGE,
	ADD_SUBCLAIM_VIEW_NO_EXPOSURE_COVERAGE_WARNING_MESSAGE,
	ADD_SUBCLAIM_VIEW_NO_LOSS_RESERVE_WARNING_MESSAGE,
} from '../../data/labelsAndMessages';
import { ClaimUpdateRequest } from '../../types/claim';
import {
	Exposure,
	ExposureCharacteristics,
	FieldGroupCreateRequest,
	FieldGroupsByLocatorMap,
	FieldValues,
	FieldValuesMap,
} from '../../types/policy';
import {
	getActiveCharacteristics,
	getCharacteristic,
	getCurrencySymbol,
	getPerilCharacteristicAtTime_T_,
	getRequestedUpdateObj,
	isExposureActive,
} from '../../util/util';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../common';
import FieldLabel from '../fields/FieldLabel';
import FieldEditorForm from '../policy/FieldEditorForm';
import { getNewFieldValues } from '../policy/fieldEditorUtils';
import ReactSelect, { SelectValue } from '../policy/fields/ReactSelect';
import LimitsForm from '../policy/Perils/LimitsForm';
import { getPerilCharacteristicsRequired } from '../policy/util_sharedEndorsementAndRenewal';
import { errorToast, successToast } from '../Toast';
import {
	checkInvalidReserveValue,
	formatRequestedReserve,
	formatReserve,
	getReserveError,
} from './util';

interface Props {
	authData: AuthData;
	claimData: ClaimData;
	policyData: PolicyData;
}

const AddSubclaimView = (props: Props) => {
	const { t } = useTranslation();
	const { policyData, claimData, authData } = props;
	const { policy, productConfig, policyViewTimestamp } = policyData;
	const { claim } = claimData;
	const policyConfig = productConfig.policyConfiguration;
	const [workflowState, setWorkflowState] = useState('selectExposure');
	const [selectedExposure, setExposure] = useState({ value: '', label: '' });
	const [coverage, setCoverage] = useState({ value: '', label: '' });
	const [expenseReserve, setExpenseReserve] = useState('0');
	const [lossReserve, setLossReserve] = useState('0');
	const [subclaimFieldValues, setSubclaimFieldValues] =
		useState<FieldValuesMap>({});
	const [fieldGroupsByLocator, setFieldGroupsByLocator] =
		useState<FieldGroupsByLocatorMap>({});
	const [locatorsByFieldGroupName, setLocatorsByFieldGroupName] =
		useState<FieldValuesMap>({});
	const navigate = useNavigate();
	const { claimConfiguration } = productConfig;
	const fieldValues = { ...subclaimFieldValues, ...locatorsByFieldGroupName };
	const canConfirm =
		workflowState === 'selectExposure'
			? selectedExposure.value !== ''
			: coverage.value !== '';
	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const updateFieldValue = (field: FieldConfiguration, value: FieldValues) => {
		const newFieldValues = getNewFieldValues(
			field,
			value,
			subclaimFieldValues,
			null,
		);
		setSubclaimFieldValues(newFieldValues);
	};

	const handleAddFieldGroup = (name: string) => {
		const currentLocators = locatorsByFieldGroupName[name] || [];
		const newLocator = `${name}-${currentLocators.length}`;
		locatorsByFieldGroupName[name] = [...currentLocators, newLocator];
		fieldGroupsByLocator[newLocator] = {};
		setFieldGroupsByLocator({ ...fieldGroupsByLocator });
		setLocatorsByFieldGroupName({ ...locatorsByFieldGroupName });
	};

	const handleRemoveFieldGroup = (
		fieldGroupLocator: string,
		index: number,
		name: string,
	) => {
		const newLocators = locatorsByFieldGroupName[name]
			?.filter((fGLocator) => fGLocator !== fieldGroupLocator)
			?.map((fGLocator, index) => {
				const newLocator = `${name}-${index}`;
				// reset fieldGroupsByLocator with new locators
				fieldGroupsByLocator[newLocator] = fieldGroupsByLocator[fGLocator];

				return newLocator;
			});

		if (newLocators) {
			delete fieldGroupsByLocator[`${name}-${newLocators.length}`]; // delete remaining field group
			locatorsByFieldGroupName[name] = newLocators;
			setLocatorsByFieldGroupName({ ...locatorsByFieldGroupName });
			setFieldGroupsByLocator({ ...fieldGroupsByLocator });
		}
	};

	const updateGroupFieldValue = (
		name: string,
		fieldGroupValues: FieldValuesMap,
		index?: number,
		fieldGroupLocator?: string,
	) => {
		fieldGroupsByLocator[fieldGroupLocator] = fieldGroupValues;
		setFieldGroupsByLocator({ ...fieldGroupsByLocator });
	};

	const getExposureInfo = (): Exposure =>
		policy.exposures.find((e) => e.locator === selectedExposure.value);

	const renderFields = () => {
		if (!claimConfiguration?.subClaimConfiguration?.fields) {
			return null;
		}

		return (
			<FieldEditorForm
				isEditable
				currentFieldValues={fieldValues}
				fieldValues={fieldValues}
				fieldGroupsByLocator={fieldGroupsByLocator}
				fieldConfigurations={claimConfiguration.subClaimConfiguration.fields}
				updateFieldValue={updateFieldValue}
				onAddFieldGroup={handleAddFieldGroup}
				removeFieldGroup={handleRemoveFieldGroup}
				updateGroupFieldValue={updateGroupFieldValue}
				mediaByLocator={null}
				configVersion={productConfig.configVersion}
			/>
		);
	};

	const renderExposureContent = () => {
		return (
			<>
				<StyledTitleContainer>
					<StyledTitle>Select {t('exposure.singular.upper')}</StyledTitle>
				</StyledTitleContainer>
				<hr />
				<StyledSectionContainer
					style={{ display: 'flex', flexDirection: 'column' }}
				>
					{renderExposureSelector()}
				</StyledSectionContainer>
			</>
		);
	};

	const renderCoverageContent = () => {
		if (workflowState !== 'specifyCoverage') {
			return null;
		}
		return (
			<>
				<StyledTitleContainer>
					<StyledTitle>Select {t('peril.singular.upper')}</StyledTitle>
				</StyledTitleContainer>
				<hr />
				<StyledSectionContainer
					style={{ display: 'flex', flexDirection: 'column' }}
				>
					{renderCoverageSelector()}
				</StyledSectionContainer>
			</>
		);
	};

	const renderExposureSelector = () => {
		return (
			<div style={{ width: '30%' }}>
				<FieldLabel
					required
					title={`Selected ${t('exposure.singular.upper')}`}
				/>
				<ReactSelect
					id='select-exposure'
					isDisabled={workflowState !== 'selectExposure'}
					options={policy.exposures.map((v) => {
						const displayName = `${v.name} ${v.displayId}`;
						return {
							value: v.locator,
							label: displayName,
						};
					})}
					value={selectedExposure}
					onChange={(opt: SelectValue) => setExposure(opt)}
				/>
			</div>
		);
	};

	const renderExposureDetails = (): any => {
		if (workflowState !== 'selectExposure' || selectedExposure.value === '') {
			return null;
		}
		const exposure = getExposureInfo();
		const isSelectedExposureActive = isExposureActive(
			exposure,
			policy,
			claim.incidentTimestamp,
		);
		const activeExposureCharacteristics = getActiveCharacteristics(
			exposure.characteristics,
		) as ExposureCharacteristics[];
		const activeCharacteristic = getCharacteristic(
			activeExposureCharacteristics,
			claim.incidentTimestamp,
		);
		const exposureConfig = policyConfig.exposures.find(
			(e) => e.name === exposure.name,
		);
		const exposureConfigFields = exposureConfig.fields;

		return (
			<>
				<StyledTitleContainer>
					<StyledTitle>{t('exposure.singular.upper')} Details</StyledTitle>
				</StyledTitleContainer>
				<hr />
				{!isSelectedExposureActive && (
					<Alert severity='warning'>
						{ADD_SUBCLAIM_VIEW_NO_EXPOSURE_COVERAGE_WARNING_MESSAGE()}
					</Alert>
				)}
				<FieldEditorForm
					isEditable={false}
					fieldValues={activeCharacteristic.fieldValues}
					fieldConfigurations={exposureConfigFields}
					updateFieldValue={null}
					updateGroupFieldValue={null}
					onAddFieldGroup={null}
					removeFieldGroup={null}
					fieldGroupsByLocator={activeCharacteristic.fieldGroupsByLocator}
					currentFieldValues={{}}
					mediaByLocator={activeCharacteristic.mediaByLocator}
					configVersion={productConfig.configVersion}
				/>
			</>
		);
	};

	const renderCoverageSelector = () => {
		const exposure = getExposureInfo();
		const exposureConfig = policyConfig.exposures.find(
			(e) => e.name === exposure.name,
		);
		const perils = exposure.perils;
		const perilOptions = perils.map((op) => {
			const displayName = exposureConfig.perils.find(
				(p) => p.name === op.name,
			).displayName;
			return { value: op.locator, label: displayName };
		});

		return (
			<div style={{ width: '30%' }}>
				<FieldLabel required title={`${t('peril.singular.upper')}`} />
				<ReactSelect
					id='coverage-selector'
					options={perilOptions}
					value={coverage}
					onChange={(option: SelectValue) => setCoverage(option)}
				/>
			</div>
		);
	};

	const renderCoverageDetails = () => {
		if (workflowState !== 'specifyCoverage' || coverage.value === '') {
			return null;
		}
		const exposure = getExposureInfo();
		const isSelectedExposureActive = isExposureActive(
			exposure,
			policy,
			policyViewTimestamp,
		);
		const peril = exposure.perils.find((p) => p.locator === coverage.value);
		const activeCharacteristics = peril.characteristics.filter(
			(c) => !c.replacedTimestamp,
		);
		const activeCharacteristic = getPerilCharacteristicAtTime_T_(
			activeCharacteristics,
			parseInt(claim.incidentTimestamp, 10),
		);
		const exposureConfig = policyConfig.exposures.find(
			(e) => e.name === exposure.name,
		);
		const perilConfig = exposureConfig.perils.find(
			(p) => p.name === peril.name,
		);
		const getNoLossReserveWarningMessage = (): string => {
			const { coverageStartTimestamp, coverageEndTimestamp } =
				getPerilCharacteristicsRequired(peril, claim.incidentTimestamp);
			return ADD_SUBCLAIM_VIEW_NO_LOSS_RESERVE_WARNING_MESSAGE()
				.replace(':start', authData.getFormattedDate(coverageStartTimestamp))
				.replace(':end', authData.getFormattedDate(coverageEndTimestamp));
		};
		return (
			<>
				<StyledTitleContainer>
					<StyledTitle>{t('peril.singular.upper')} Details</StyledTitle>
				</StyledTitleContainer>
				<hr />
				{!isSelectedExposureActive && (
					<Alert severity='warning'>{getNoLossReserveWarningMessage()}</Alert>
				)}
				<StyledSectionContainer
					style={{ display: 'flex', flexDirection: 'column' }}
				>
					{!hidePerilLimitsEnabled && (
						<LimitsForm
							perilObj={activeCharacteristic}
							id={peril.locator}
							isInEditMode={false}
							perilConfig={activeCharacteristic}
							currency={policy?.currency}
							userHasWriteAccess={false}
							updateLimit={null}
						/>
					)}
					<FieldEditorForm
						isEditable={false}
						fieldValues={activeCharacteristic.fieldValues}
						fieldConfigurations={perilConfig.fields}
						updateFieldValue={null}
						updateGroupFieldValue={null}
						onAddFieldGroup={null}
						removeFieldGroup={null}
						fieldGroupsByLocator={activeCharacteristic.fieldGroupsByLocator}
						currentFieldValues={{}}
						mediaByLocator={activeCharacteristic.mediaByLocator}
						configVersion={productConfig.configVersion}
					/>
				</StyledSectionContainer>
			</>
		);
	};

	const renderReserves = () => {
		if (workflowState !== 'specifyCoverage' || coverage.value === '') {
			return null;
		}
		const currencyVal = getCurrencySymbol(policy?.currency);
		const exposure = getExposureInfo();
		const isSelectedExposureActive = isExposureActive(
			exposure,
			policy,
			policyViewTimestamp,
		);
		return (
			<>
				<StyledTitleContainer>
					<StyledTitle>Set Reserves</StyledTitle>
				</StyledTitleContainer>
				<hr />
				<StyledSectionContainer style={{ display: 'flex' }}>
					<TextField
						error={checkInvalidReserveValue(lossReserve)}
						id={'lossReserve'}
						type='number'
						style={{ width: '30%', marginRight: '10px' }}
						label={
							<div>
								{ADD_SUBCLAIM_VIEW_LOSS_RESERVE_LABEL()}
								{!isSelectedExposureActive && (
									<Tooltip
										title={ADD_SUBCLAIM_VIEW_LOSS_RESERVE_TOOLTIP_MESSAGE()}
										placement='top'
									>
										<IconButton>
											<InfoIcon />
										</IconButton>
									</Tooltip>
								)}
							</div>
						}
						value={lossReserve}
						disabled={!isSelectedExposureActive}
						onChange={(e) => setLossReserve(e.target.value)}
						onBlur={() => setLossReserve(formatReserve(lossReserve))}
						helperText={getReserveError(lossReserve)}
						InputProps={{
							startAdornment: (
								<InputAdornment position='start'>{currencyVal}</InputAdornment>
							),
						}}
					/>
					<TextField
						error={checkInvalidReserveValue(expenseReserve)}
						id={'expenseReserve'}
						type='number'
						style={{ width: '30%' }}
						label={ADD_SUBCLAIM_VIEW_EXPENSE_RESERVE_LABEL()}
						value={expenseReserve}
						disabled={false}
						onChange={(e) => setExpenseReserve(e.target.value)}
						onBlur={() => setExpenseReserve(formatReserve(expenseReserve))}
						helperText={getReserveError(expenseReserve)}
						InputProps={{
							startAdornment: (
								<InputAdornment position='start'>{currencyVal}</InputAdornment>
							),
						}}
					/>
				</StyledSectionContainer>
			</>
		);
	};

	const renderContent = () => {
		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{renderFields()}
				{renderExposureContent()}
				{renderExposureDetails()}
				{renderCoverageContent()}
				{renderCoverageDetails()}
				{renderReserves()}
			</div>
		);
	};

	const addSubclaim = async () => {
		if (!canConfirm) return;
		let fieldGroups: FieldGroupCreateRequest[] = [];
		// go through added field groups and create the fieldGroups property for ClaimUpdateRequest.addSubclaims
		for (const [name, locators] of Object.entries(locatorsByFieldGroupName)) {
			const newFieldGroups = locators
				.map((locator) => {
					const fieldValues = fieldGroupsByLocator[locator] || null;
					return fieldValues ? { fieldName: name, fieldValues } : null;
				})
				.filter((fg) => !!fg);
			fieldGroups = [...fieldGroups, ...newFieldGroups];
		}

		const expenseReserveVal = formatRequestedReserve(expenseReserve);
		const lossReserveVal = formatRequestedReserve(lossReserve);
		const payload: ClaimUpdateRequest = {
			addSubClaims: [
				getRequestedUpdateObj(
					{
						perilLocator: coverage.value,
						expenseReserve: {
							amount: expenseReserveVal.toString(),
						},
						lossReserve: {
							amount: lossReserveVal.toString(),
						},
						fieldValues: subclaimFieldValues,
						fieldGroups,
					},
					policyData.productConfig.claimConfiguration.subClaimConfiguration
						?.fields,
				),
			],
		};
		try {
			await claimData.setLoading(true);
			// update claim with new subclaim
			await claimData.updateClaim(claim.locator, payload);
			successToast('Successfully added subclaim');
			await claimData.setLoading(false);
			navigate(`../subclaims`);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to add subclaim');
		}
	};

	const cancelSubClaim = () => {
		navigate(-1);
	};

	const navButtons = () => {
		const backgroundColor = canConfirm ? '#02457A' : 'rgb(2, 69, 122, 0.44)';
		const cursor = canConfirm ? 'pointer' : 'not-allowed';
		const nextAction = () =>
			canConfirm ? setWorkflowState('specifyCoverage') : null;
		return (
			<>
				<Button
					color='primary'
					style={PRIMARY_BUTTON_STYLE}
					onClick={cancelSubClaim}
				>
					{' '}
					Cancel
				</Button>
				{workflowState === 'selectExposure' && (
					<Button
						color='primary'
						style={{ backgroundColor, color: '#FFFFFF', margin: '5px', cursor }}
						onClick={nextAction}
						disabled={!canConfirm}
					>
						{' '}
						Next
					</Button>
				)}
				{workflowState === 'specifyCoverage' && (
					<Button
						color='primary'
						style={{ backgroundColor, color: '#FFFFFF', margin: '5px', cursor }}
						onClick={addSubclaim}
						disabled={!canConfirm}
					>
						{' '}
						Add Subclaim
					</Button>
				)}
			</>
		);
	};

	return (
		<Stack spacing={2}>
			<Stack direction='row' justifyContent='flex-end'>
				{navButtons()}
			</Stack>
			{renderContent()}
		</Stack>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'authData' | 'claimData' | 'policyData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<PolicyContext.Consumer>
					{(policyData) => (
						<ClaimContext.Consumer>
							{(claimData) => (
								<Component
									{...props}
									authData={authData}
									policyData={policyData}
									claimData={claimData}
								/>
							)}
						</ClaimContext.Consumer>
					)}
				</PolicyContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(AddSubclaimView);
